import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import TaskEmailService from '../../services/TaskEmailService';
import { taskErrorMessage } from '../../services/TaskTrackingService';

const GROUPS = ['Tasks', 'Requests and videos', 'Posting', 'Digest'];

const RESULTS = [
    ['SENT', 'Sent'],
    ['FAILED', 'Failed'],
    ['SERVER_OFF', 'Not sent: email off on server'],
    ['PAUSED', 'Not sent: admin paused all emails'],
    ['POINT_OFF', 'Not sent: admin turned this email off'],
    ['THEIR_SETTINGS', 'Not sent: their settings'],
    ['TEST', 'Test'],
];

/** "Today 10:42", "Mon 08:00", or "3 Oct 08:00" for older. */
export const whenText = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    const days = (new Date().setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86400000;
    if (days === 0) return `Today ${time}`;
    if (days < 7) return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
    return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
};

const Card = ({ title, description, children }) => (
    <Paper sx={{ p: 3 }}>
        <Typography variant="h6">{title}</Typography>
        {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {description}
            </Typography>
        )}
        {children}
    </Paper>
);

/** An email's HTML, sandboxed with no scripts, as the digest preview does. */
export const EmailFrame = ({ html }) => (
    <Box
        component="iframe"
        title="Email"
        sandbox=""
        srcDoc={html || ''}
        sx={{ width: '100%', height: 480, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
    />
);

const serverReason = (server) =>
    server === 'STAGING' ? 'Staging never sends email' : server === 'OFF' ? 'Email is off on this server' : null;

// ------------------------------------------------------------------ Sending

const Sending = ({ status, onChange }) => {
    const [confirmPause, setConfirmPause] = useState(false);
    const [test, setTest] = useState(null);
    const [error, setError] = useState(null);
    const offReason = serverReason(status.server);
    const last = status.last;

    const setPaused = async (paused) => {
        setConfirmPause(false);
        setError(null);
        try {
            onChange(await TaskEmailService.update({ paused }));
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const sendTest = async () => {
        setTest({ sending: true });
        try {
            const r = await TaskEmailService.test(null);
            setTest({ ok: `Sent at ${whenText(r.sentAt).replace('Today ', '')}. Check the inbox.` });
        } catch (e) {
            setTest({ error: taskErrorMessage(e) });
        }
    };

    return (
        <Card title="Sending">
            <Typography variant="body2">Task emails go out from {status.from}.</Typography>
            {offReason && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    {offReason}. Only whoever runs the server can change this.
                </Alert>
            )}
            {last && last.result !== 'FAILED' && (
                <Typography variant="body2" color="text.secondary">
                    Last email sent {whenText(last.at).replace('Today', 'today at')}.
                </Typography>
            )}
            {last?.result === 'FAILED' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    Last email failed {whenText(last.at).replace('Today', 'today at')}: {last.error}. If it says the
                    password wasn't accepted, the App Password may have been revoked. See the email log below.
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
                <Switch
                    checked={!status.paused}
                    disabled={!!offReason}
                    onChange={(e) => (e.target.checked ? setPaused(false) : setConfirmPause(true))}
                    inputProps={{ 'aria-label': 'Send task emails' }}
                />
                <Box sx={{ pt: 1 }}>
                    <Typography variant="body1">Send task emails</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {status.paused
                            ? `Paused by ${status.pausedBy || 'an admin'} ${whenText(status.pausedAt)
                                  .replace('Today', 'today at')
                                  .trim()}. No task emails are going out.`
                            : 'Off stops every task email at once. Nothing is lost: what would have gone out shows in the log as "Not sent: admin paused all emails".'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={sendTest} disabled={!!offReason || test?.sending}>
                    Send a test email to me
                </Button>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                    goes only to your own address
                </Typography>
                <Box role="status" aria-live="polite" sx={{ mt: 1 }}>
                    {test?.ok && <Alert severity="success">{test.ok}</Alert>}
                    {test?.error && <Alert severity="error">{test.error}</Alert>}
                </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

            <Dialog open={confirmPause} onClose={() => setConfirmPause(false)}>
                <DialogTitle>Stop all task emails for everyone?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Nothing is lost. Each email that would have gone out shows in the log until you turn sending back on.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmPause(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => setPaused(true)}>
                        Pause emails
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

// ------------------------------------------------------------------ Preview

const PreviewDialog = ({ point, server, onClose }) => {
    const [mail, setMail] = useState(null);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(null);
    const offReason = serverReason(server);
    const digest = point?.key === 'WEEKLY_DIGEST';

    useEffect(() => {
        if (!point) return;
        setMail(null);
        setError(null);
        setSent(null);
        // The digest shows the admin's own real one; the rest are made from sample data
        (digest ? TaskEmailService.digestPreview() : TaskEmailService.preview(point.key))
            .then(setMail)
            .catch((e) => setError(taskErrorMessage(e)));
    }, [point, digest]);

    const sendToMe = async () => {
        setSent({ sending: true });
        try {
            await TaskEmailService.test(point.key);
            setSent({ ok: 'Sent to you, with [Test] at the start of the subject.' });
        } catch (e) {
            setSent({ error: taskErrorMessage(e) });
        }
    };

    return (
        <Dialog open={!!point} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {point?.label} <Chip size="small" label={digest ? 'Your digest' : 'Sample'} sx={{ ml: 1 }} />
            </DialogTitle>
            <DialogContent>
                {!mail && !error && <CircularProgress size={24} />}
                {error && <Alert severity="error">{error}</Alert>}
                {mail && (
                    <>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <b>Subject:</b> {mail.subject}
                        </Typography>
                        {digest && mail.empty && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                Your digest is empty this week, so you wouldn't get one.
                            </Alert>
                        )}
                        <EmailFrame html={mail.html} />
                    </>
                )}
                <Box role="status" aria-live="polite" sx={{ mt: 1 }}>
                    {sent?.ok && <Alert severity="success">{sent.ok}</Alert>}
                    {sent?.error && <Alert severity="error">{sent.error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                {offReason && (
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 2 }}>
                        {offReason}
                    </Typography>
                )}
                <Button onClick={sendToMe} disabled={!!offReason || sent?.sending}>
                    Send to me
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ------------------------------------------------------------------ When emails are sent

const Points = ({ status, onChange }) => {
    const [saved, setSaved] = useState({});
    const [preview, setPreview] = useState(null);
    const [confirmDigest, setConfirmDigest] = useState(false);
    const [digestResult, setDigestResult] = useState(null);

    const flip = async (point, on) => {
        setSaved((s) => ({ ...s, [point.key]: 'Saving' }));
        try {
            onChange(await TaskEmailService.update({ points: { [point.key]: on } }));
            setSaved((s) => ({ ...s, [point.key]: 'Saved' }));
        } catch (e) {
            setSaved((s) => ({ ...s, [point.key]: taskErrorMessage(e) }));
        }
    };

    const runDigest = async () => {
        setConfirmDigest(false);
        setDigestResult(null);
        try {
            const r = await TaskEmailService.runDigest();
            setDigestResult({ ok: `Sent to ${r.sent} PM${r.sent === 1 ? '' : 's'}.` });
        } catch (e) {
            setDigestResult({ error: taskErrorMessage(e) });
        }
    };

    return (
        <Card title="When emails are sent">
            {GROUPS.map((group) => (
                <Box key={group} sx={{ mt: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                        {group}
                    </Typography>
                    {status.points
                        .filter((p) => p.group === group)
                        .map((p) => (
                            <Box
                                key={p.key}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.25 }}
                            >
                                <Switch
                                    checked={p.on}
                                    onChange={(e) => flip(p, e.target.checked)}
                                    inputProps={{ 'aria-label': p.label }}
                                />
                                <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                    <Typography variant="body2">{p.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {p.who}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                    role="status"
                                    aria-live="polite"
                                    color={saved[p.key] && saved[p.key] !== 'Saved' && saved[p.key] !== 'Saving' ? 'error' : 'text.secondary'}
                                >
                                    {saved[p.key]}
                                </Typography>
                                <Button size="small" onClick={() => setPreview(p)}>
                                    Preview
                                </Button>
                                {p.key === 'WEEKLY_DIGEST' && (
                                    <Button size="small" onClick={() => setConfirmDigest(true)}>
                                        Send now
                                    </Button>
                                )}
                            </Box>
                        ))}
                </Box>
            ))}
            {digestResult?.ok && <Alert severity="success" sx={{ mt: 1 }}>{digestResult.ok}</Alert>}
            {digestResult?.error && <Alert severity="error" sx={{ mt: 1 }}>{digestResult.error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                People can also turn emails off for themselves in My email settings. A switch here stops an email for
                everyone.
            </Typography>

            <PreviewDialog point={preview} server={status.server} onClose={() => setPreview(null)} />
            <Dialog open={confirmDigest} onClose={() => setConfirmDigest(false)}>
                <DialogTitle>Send this week's digest to all PMs now?</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmDigest(false)}>Cancel</Button>
                    <Button variant="contained" onClick={runDigest}>
                        Send now
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

// ------------------------------------------------------------------ Email log

const LogRowDialog = ({ id, onClose }) => {
    const [row, setRow] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!id) return;
        setRow(null);
        setError(null);
        TaskEmailService.logRow(id)
            .then(setRow)
            .catch((e) => setError(taskErrorMessage(e)));
    }, [id]);

    return (
        <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{row?.subject || 'Email'}</DialogTitle>
            <DialogContent>
                {!row && !error && <CircularProgress size={24} />}
                {error && <Alert severity="error">{error}</Alert>}
                {row && (
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                            <b>To:</b> {row.to}
                        </Typography>
                        <Typography variant="body2">
                            <b>When:</b> {new Date(row.at).toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                            <b>Email:</b> {row.pointLabel}
                            {row.sentBy && ` (sent by ${row.sentBy})`}
                        </Typography>
                        <Typography variant="body2">
                            <b>Result:</b> {row.resultLabel}
                        </Typography>
                        {row.error && <Alert severity="error">{row.error}</Alert>}
                        {row.taskId && row.taskId !== 'sample' && (
                            <Typography variant="body2">
                                <a href={`/tasks?view=all&task=${encodeURIComponent(row.taskId)}`}>Open task</a>
                            </Typography>
                        )}
                    </Stack>
                )}
                {row?.html ? (
                    <EmailFrame html={row.html} />
                ) : (
                    row && (
                        <Typography variant="body2" color="text.secondary">
                            This email wasn't made, because it was stopped before it was needed.
                        </Typography>
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const EmailLog = ({ points }) => {
    const [filters, setFilters] = useState({ days: 7, point: '', result: '', q: '' });
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(null);

    // Search waits until typing stops
    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => ({ ...f, q: query })), 400);
        return () => clearTimeout(t);
    }, [query]);

    const load = useCallback(() => {
        setError(null);
        TaskEmailService.log({ ...filters, page })
            .then(setData)
            .catch((e) => setError(taskErrorMessage(e)));
    }, [filters, page]);

    useEffect(load, [load]);

    const set = (key) => (e) => {
        setPage(0);
        setFilters((f) => ({ ...f, [key]: e.target.value }));
    };

    const pages = data ? Math.ceil(data.total / data.pageSize) : 0;

    return (
        <Card title="Email log" description="Every task email this server sent, or decided not to send, newest first. Kept for 90 days.">
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
                <TextField select size="small" label="When" value={filters.days} onChange={set('days')} sx={{ minWidth: 140 }}>
                    <MenuItem value={1}>Today</MenuItem>
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                </TextField>
                <TextField select size="small" label="Email" value={filters.point} onChange={set('point')} sx={{ minWidth: 180 }}>
                    <MenuItem value="">All emails</MenuItem>
                    {points.map((p) => (
                        <MenuItem key={p.key} value={p.key}>
                            {p.label}
                        </MenuItem>
                    ))}
                    <MenuItem value="TEST">Test</MenuItem>
                </TextField>
                <TextField select size="small" label="Result" value={filters.result} onChange={set('result')} sx={{ minWidth: 180 }}>
                    <MenuItem value="">All results</MenuItem>
                    {RESULTS.map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="Search address"
                    value={query}
                    onChange={(e) => {
                        setPage(0);
                        setQuery(e.target.value);
                    }}
                />
                <Button size="small" onClick={load}>
                    Refresh
                </Button>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {!data && !error && <CircularProgress size={24} />}
            {data?.rows.length === 0 && <Typography variant="body2">No emails match.</Typography>}
            {data?.rows.map((r) => (
                <Box
                    key={r.id}
                    component="button"
                    type="button"
                    onClick={() => setOpen(r.id)}
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        width: '100%',
                        textAlign: 'left',
                        py: 0.75,
                        px: 1,
                        border: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'transparent',
                        color: 'inherit',
                        font: 'inherit',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                    }}
                >
                    <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>
                        {whenText(r.at)}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {r.to}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 160px' }}>
                        {r.pointLabel}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ flex: '1 1 200px' }}
                        color={r.result === 'FAILED' ? 'error' : r.result === 'SENT' || r.result === 'TEST' ? 'text.primary' : 'text.secondary'}
                    >
                        {r.resultLabel}
                    </Typography>
                </Box>
            ))}
            {pages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Button size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                        Newer
                    </Button>
                    <Typography variant="caption">
                        Page {page + 1} of {pages}
                    </Typography>
                    <Button size="small" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                        Older
                    </Button>
                </Box>
            )}
            <LogRowDialog id={open} onClose={() => setOpen(null)} />
        </Card>
    );
};

/** Task tools, Emails: admins only (docs/task-email-design.md, E2 and E3). */
const EmailsTab = () => {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        TaskEmailService.status()
            .then(setStatus)
            .catch((e) => setError(taskErrorMessage(e)));
    }, []);

    if (error) return <Alert severity="error">{error}</Alert>;
    if (!status) return <CircularProgress />;
    return (
        <>
            <Sending status={status} onChange={setStatus} />
            <Points status={status} onChange={setStatus} />
            <EmailLog points={status.points} />
        </>
    );
};

export default EmailsTab;
